import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "modal-body-scroll" }
const _hoisted_2 = { class: "meeting-details-card" }
const _hoisted_3 = { class: "detail-row" }
const _hoisted_4 = { class: "detail-row" }
const _hoisted_5 = { class: "detail-value" }
const _hoisted_6 = { class: "detail-row" }
const _hoisted_7 = { class: "detail-value" }
const _hoisted_8 = { class: "detail-row" }
const _hoisted_9 = { class: "detail-value" }
const _hoisted_10 = {
  key: 1,
  class: "text-muted"
}
const _hoisted_11 = { class: "detail-row" }
const _hoisted_12 = {
  key: 0,
  class: "detail-value link-row"
}
const _hoisted_13 = ["href"]
const _hoisted_14 = {
  key: 1,
  class: "detail-value text-muted"
}
const _hoisted_15 = { class: "form-grid" }
const _hoisted_16 = { class: "form-group" }
const _hoisted_17 = { class: "form-group" }
const _hoisted_18 = { class: "form-group" }
const _hoisted_19 = { class: "form-group" }
const _hoisted_20 = {
  key: 0,
  class: "after-meeting-warning"
}
const _hoisted_21 = { class: "modal-actions" }

import { ref, computed, watch, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { Icon } from "@iconify/vue";
import { Field, Form, ErrorMessage, defineRule } from "vee-validate";
import { Clipboard } from "v-clipboard";
import { Button, Modal, Select, DateSelector } from "@/components/ui";
import {
  useBuckets,
  useBucketOptions,
  useRegisterBucket,
  useUpdateBucket,
} from "@/composables/bucket";
import { MEETINGS_ROUTE } from "@/router/routes";
import { TaskType } from "@/utils/enums";
import { getTaskTypeIcon } from "@/utils/iconMapping";

interface Props {
  selectedMeeting?: {
    id: string;
    title: string;
    duration: number;
    meetingDateTime: string;
    guestEmails?: string[];
    meetingURL?: string;
    contactId: string;
  };
}


export default /*@__PURE__*/_defineComponent({
  __name: 'ScheduleMeetingReminderModal',
  props: {
    selectedMeeting: {}
  },
  emits: ["closeScheduleMeetingReminderModal"],
  setup(__props: any, { emit: __emit }) {

defineRule("required", (value: any, [fieldName = "Field"]: string[]) => {
  if (!value || value.length === 0) {
    return `${fieldName} is required`;
  }
  return true;
});

const props = __props;
const emit = __emit;

const router = useRouter();
const store = useStore();

const showModal = ref(true);
const selectedFollowUpOption = ref<string | number | undefined>(undefined);

const account = computed(() => store.getters.account.data);
const organizationId = computed(() => account.value?.organizationId);

// Form data
const formData = ref({
  id: null as string | null,
  taskType: null as string | null,
  followUpDate: null as string | null,
  bucketOptionId: null as string | null,
  contactIds: [] as string[],
  organizationId: null as string | null,
});

// Queries
const bucketParams = computed(() =>
  organizationId.value && props.selectedMeeting?.contactId
    ? { organizationId: organizationId.value, contactId: props.selectedMeeting.contactId }
    : null
);

const { data: bucketsData } = useBuckets(bucketParams);
const { data: bucketOptionsData } = useBucketOptions(
  computed(() => (organizationId.value ? { organizationId: organizationId.value } : null))
);

// Mutations
const { mutateAsync: registerBucket, isPending: isRegistering } = useRegisterBucket();
const { mutateAsync: updateBucket, isPending: isUpdating } = useUpdateBucket();

const isLoading = computed(() => isRegistering.value || isUpdating.value);

// Computed
const existingBucket = computed(() => {
  if (!bucketsData.value?.data?.length) return null;
  return bucketsData.value.data[0];
});

const bucketOptionsForSelect = computed(() => {
  if (!bucketOptionsData.value) return [];
  return bucketOptionsData.value.map((opt: any) => ({
    value: opt.id,
    label: opt.name,
  }));
});

const taskTypeOptions = computed(() =>
  Object.values(TaskType).map((task) => ({
    value: task,
    label: task.charAt(0).toUpperCase() + task.slice(1),
    icon: getTaskTypeIcon(task),
  }))
);

const followUpOptions = [
  { value: "1", label: "Tomorrow" },
  { value: "3", label: "In 3 Days" },
  { value: "7", label: "In 1 Week" },
  { value: "30", label: "In 1 Month" },
  { value: "90", label: "In 3 Months" },
  { value: "180", label: "In 6 Months" },
  { value: "365", label: "In 1 Year" },
];

// Get meeting date for comparison (date only, no time)
const meetingDate = computed(() => {
  if (!props.selectedMeeting?.meetingDateTime) return null;
  const date = new Date(props.selectedMeeting.meetingDateTime);
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
});

// Meeting date as YYYY-MM-DD for DateSelector maxDate
const meetingDateIso = computed(() => {
  if (!meetingDate.value) return undefined;
  return meetingDate.value.toISOString().split("T")[0];
});

const isFollowUpAfterMeeting = computed(() => {
  if (!meetingDate.value || !formData.value.followUpDate) return false;
  const followUp = new Date(formData.value.followUpDate);
  return followUp > meetingDate.value;
});

// Methods
const formatDate = (dateString: string) => {
  if (!dateString) return "—";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const truncateUrl = (url: string) => {
  if (url.length > 35) {
    return url.substring(0, 35) + "...";
  }
  return url;
};

const openLink = (url: string) => {
  window.open(url, "_blank", "noopener,noreferrer");
};

const copyToClipboard = (value: string) => {
  Clipboard.copy(value);
};

const updateFollowUpDate = (value: string | number) => {
  if (value == null || value === "") return;
  const daysToAdd = parseInt(String(value), 10);
  const date = new Date();
  date.setDate(date.getDate() + daysToAdd);
  const localMidnight = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
  if (meetingDate.value && localMidnight > meetingDate.value) {
    formData.value.followUpDate = meetingDate.value.toISOString();
  } else {
    formData.value.followUpDate = localMidnight.toISOString();
  }
};

const setInitialValues = () => {
  if (!props.selectedMeeting) return;

  // Always set these for creating new buckets
  formData.value.contactIds = [props.selectedMeeting.contactId];
  formData.value.organizationId = organizationId.value;

  if (existingBucket.value) {
    formData.value.id = existingBucket.value.id;
    formData.value.taskType = existingBucket.value.taskType;
    formData.value.bucketOptionId = existingBucket.value.bucketOptionId;
    if (existingBucket.value.followUpDate) {
      formData.value.followUpDate = existingBucket.value.followUpDate;
    }
  }
};

// Watch for bucket data to load and update form values
watch(
  existingBucket,
  (bucket) => {
    if (bucket) {
      formData.value.id = bucket.id;
      formData.value.taskType = bucket.taskType;
      formData.value.bucketOptionId = bucket.bucketOptionId;
      if (bucket.followUpDate) {
        formData.value.followUpDate = bucket.followUpDate;
      }
    }
  },
  { immediate: true }
);

const handleSubmit = async () => {
  if (isFollowUpAfterMeeting.value) {
    return;
  }

  try {
    if (existingBucket.value && formData.value.id) {
      await updateBucket({
        id: formData.value.id,
        taskType: formData.value.taskType!,
        followUpDate: formData.value.followUpDate!,
        bucketOptionId: formData.value.bucketOptionId!,
      });
    } else {
      await registerBucket({
        taskType: formData.value.taskType!,
        followUpDate: formData.value.followUpDate!,
        bucketOptionId: formData.value.bucketOptionId!,
        contactIds: formData.value.contactIds,
        organizationId: formData.value.organizationId!,
      });
    }
    closeModal();
    router.push({ name: MEETINGS_ROUTE });
  } catch (error: any) {
    // Save failed
  }
};

const closeModal = () => {
  emit("closeScheduleMeetingReminderModal");
};

// Watch for bucket data and set initial values
onMounted(() => {
  setInitialValues();
});

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createBlock(_unref(Modal), {
    show: showModal.value && !!__props.selectedMeeting,
    size: "md",
    zIndex: 10080,
    "dialog-max-width": "560px",
    "content-max-height": "75vh",
    onClose: closeModal,
    title: "Schedule a Meeting Reminder"
  }, {
    footer: _withCtx(() => [
      _createElementVNode("div", _hoisted_21, [
        _createVNode(_unref(Button), {
          type: "button",
          variant: "secondary",
          block: "",
          onClick: closeModal,
          disabled: isLoading.value
        }, {
          default: _withCtx(() => [...(_cache[11] || (_cache[11] = [
            _createTextVNode(" Cancel ", -1)
          ]))]),
          _: 1
        }, 8, ["disabled"]),
        _createVNode(_unref(Button), {
          type: "submit",
          form: "schedule-meeting-reminder-form",
          variant: "primary",
          block: "",
          loading: isLoading.value,
          disabled: isLoading.value || isFollowUpAfterMeeting.value
        }, {
          default: _withCtx(() => [...(_cache[12] || (_cache[12] = [
            _createTextVNode(" Save ", -1)
          ]))]),
          _: 1
        }, 8, ["loading", "disabled"])
      ])
    ]),
    default: _withCtx(() => [
      (__props.selectedMeeting)
        ? (_openBlock(), _createBlock(_unref(Form), {
            key: 0,
            id: "schedule-meeting-reminder-form",
            class: "form",
            onSubmit: handleSubmit
          }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_1, [
                _createElementVNode("div", _hoisted_2, [
                  _createElementVNode("div", _hoisted_3, [
                    _cache[6] || (_cache[6] = _createElementVNode("span", { class: "detail-label" }, "Meeting Title", -1)),
                    _createVNode(_component_router_link, {
                      to: { name: _unref(MEETINGS_ROUTE), params: { id: __props.selectedMeeting.id } },
                      class: "detail-value link"
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(__props.selectedMeeting.title), 1)
                      ]),
                      _: 1
                    }, 8, ["to"])
                  ]),
                  _createElementVNode("div", _hoisted_4, [
                    _cache[7] || (_cache[7] = _createElementVNode("span", { class: "detail-label" }, "Duration", -1)),
                    _createElementVNode("span", _hoisted_5, _toDisplayString(__props.selectedMeeting.duration) + " min", 1)
                  ]),
                  _createElementVNode("div", _hoisted_6, [
                    _cache[8] || (_cache[8] = _createElementVNode("span", { class: "detail-label" }, "Scheduled on", -1)),
                    _createElementVNode("span", _hoisted_7, _toDisplayString(formatDate(__props.selectedMeeting.meetingDateTime)), 1)
                  ]),
                  _createElementVNode("div", _hoisted_8, [
                    _cache[9] || (_cache[9] = _createElementVNode("span", { class: "detail-label" }, "Guests", -1)),
                    _createElementVNode("span", _hoisted_9, [
                      ((__props.selectedMeeting.guestEmails ?? []).length > 0)
                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                            _createTextVNode(_toDisplayString((__props.selectedMeeting.guestEmails ?? []).join(", ")), 1)
                          ], 64))
                        : (_openBlock(), _createElementBlock("span", _hoisted_10, "—"))
                    ])
                  ]),
                  _createElementVNode("div", _hoisted_11, [
                    _cache[10] || (_cache[10] = _createElementVNode("span", { class: "detail-label" }, "Meeting Link", -1)),
                    (__props.selectedMeeting.meetingURL)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                          _createElementVNode("a", {
                            href: __props.selectedMeeting.meetingURL,
                            target: "_blank",
                            rel: "noopener noreferrer",
                            class: "meeting-link"
                          }, _toDisplayString(truncateUrl(__props.selectedMeeting.meetingURL)), 9, _hoisted_13),
                          _createElementVNode("button", {
                            type: "button",
                            class: "icon-btn",
                            onClick: _cache[0] || (_cache[0] = _withModifiers(($event: any) => (openLink(__props.selectedMeeting.meetingURL)), ["stop"]))
                          }, [
                            _createVNode(_unref(Icon), { icon: "mdi:open-in-new" })
                          ]),
                          _createElementVNode("button", {
                            type: "button",
                            class: "icon-btn",
                            onClick: _cache[1] || (_cache[1] = _withModifiers(($event: any) => (copyToClipboard(__props.selectedMeeting.meetingURL)), ["stop"]))
                          }, [
                            _createVNode(_unref(Icon), { icon: "mdi:content-copy" })
                          ])
                        ]))
                      : (_openBlock(), _createElementBlock("span", _hoisted_14, "—"))
                  ])
                ]),
                _createElementVNode("div", _hoisted_15, [
                  _createElementVNode("div", _hoisted_16, [
                    _createVNode(_unref(Field), {
                      name: "bucketOptionId",
                      rules: { required: ['Bucket'] },
                      modelValue: formData.value.bucketOptionId,
                      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((formData.value.bucketOptionId) = $event))
                    }, {
                      default: _withCtx(({ field }) => [
                        _createVNode(_unref(Select), {
                          "model-value": field.value,
                          "onUpdate:modelValue": ($event: any) => (field.onChange($event)),
                          onBlur: field.onBlur,
                          options: bucketOptionsForSelect.value,
                          label: "Bucket",
                          placeholder: "Select bucket",
                          required: ""
                        }, null, 8, ["model-value", "onUpdate:modelValue", "onBlur", "options"])
                      ]),
                      _: 1
                    }, 8, ["modelValue"]),
                    _createVNode(_unref(ErrorMessage), {
                      name: "bucketOptionId",
                      class: "error-message"
                    })
                  ]),
                  _createElementVNode("div", _hoisted_17, [
                    _createVNode(_unref(Field), {
                      name: "taskType",
                      rules: { required: ['Task Type'] },
                      modelValue: formData.value.taskType,
                      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((formData.value.taskType) = $event))
                    }, {
                      default: _withCtx(({ field }) => [
                        _createVNode(_unref(Select), {
                          "model-value": field.value,
                          "onUpdate:modelValue": ($event: any) => (field.onChange($event)),
                          onBlur: field.onBlur,
                          options: taskTypeOptions.value,
                          label: "Task Type",
                          placeholder: "Select task type",
                          required: ""
                        }, null, 8, ["model-value", "onUpdate:modelValue", "onBlur", "options"])
                      ]),
                      _: 1
                    }, 8, ["modelValue"]),
                    _createVNode(_unref(ErrorMessage), {
                      name: "taskType",
                      class: "error-message"
                    })
                  ]),
                  _createElementVNode("div", _hoisted_18, [
                    _createVNode(_unref(Select), {
                      modelValue: selectedFollowUpOption.value,
                      "onUpdate:modelValue": [
                        _cache[4] || (_cache[4] = ($event: any) => ((selectedFollowUpOption).value = $event)),
                        updateFollowUpDate
                      ],
                      options: followUpOptions,
                      label: "Follow up in",
                      placeholder: "Select follow up option"
                    }, null, 8, ["modelValue"])
                  ]),
                  _createElementVNode("div", _hoisted_19, [
                    _createVNode(_unref(Field), {
                      name: "followUpDate",
                      rules: { required: ['Follow up Date'] },
                      modelValue: formData.value.followUpDate,
                      "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((formData.value.followUpDate) = $event))
                    }, {
                      default: _withCtx(({ field }) => [
                        _createVNode(_unref(DateSelector), {
                          "model-value": field.value,
                          "onUpdate:modelValue": ($event: any) => (field.onChange($event)),
                          onBlur: field.onBlur,
                          label: "Follow up Date",
                          "disable-past": false,
                          "max-date": meetingDateIso.value,
                          required: ""
                        }, null, 8, ["model-value", "onUpdate:modelValue", "onBlur", "max-date"])
                      ]),
                      _: 1
                    }, 8, ["modelValue"]),
                    _createVNode(_unref(ErrorMessage), {
                      name: "followUpDate",
                      class: "error-message"
                    })
                  ])
                ]),
                (isFollowUpAfterMeeting.value)
                  ? (_openBlock(), _createElementBlock("p", _hoisted_20, " Follow-up date must be on or before the meeting date. "))
                  : _createCommentVNode("", true)
              ])
            ]),
            _: 1
          }))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }, 8, ["show"]))
}
}

})